import { allFromSnapshot, oneFromSnapshot, } from "@buildwithflux/firestore-compatibility-layer";
import { ExecutionId, } from "@buildwithflux/models";
import { omit } from "lodash";
/**
 * Transform raw Firestore data to typed IChatMessageThreadData.
 * Converts executionId from string to ExecutionId type.
 */
export function deserializeThreadData(raw) {
    if (!raw.latest_brief || raw.latest_brief.executionId === undefined) {
        return raw;
    }
    const { executionId: rawExecutionId, ...restLatestBrief } = raw.latest_brief;
    return {
        ...raw,
        latest_brief: {
            ...restLatestBrief,
            executionId: ExecutionId.deserialize(rawExecutionId),
        },
    };
}
/**
 * Transform typed IChatMessageThreadData to raw Firestore data.
 * Converts executionId from ExecutionId type to string.
 */
function serializeThreadData(data) {
    if (!data.latest_brief || data.latest_brief.executionId === undefined) {
        return data;
    }
    const { executionId, ...restLatestBrief } = data.latest_brief;
    return {
        ...data,
        latest_brief: {
            ...restLatestBrief,
            executionId: ExecutionId.serialize(executionId),
        },
    };
}
export class FirestoreChatRepository {
    db;
    logger;
    constructor(db, logger) {
        this.db = db;
        this.logger = logger;
    }
    /** @inheritDoc */
    async getThreads(documentUid) {
        const rawThreads = allFromSnapshot(await this.db.documentChatThreadsCollection(documentUid).get());
        return rawThreads.map((raw) => deserializeThreadData(raw));
    }
    /** @inheritDoc */
    async getThread(documentUid, threadUid) {
        const raw = oneFromSnapshot(await this.db.documentChatThreadsCollection(documentUid).doc(threadUid).get(), () => undefined);
        return raw ? deserializeThreadData(raw) : undefined;
    }
    /** @inheritDoc */
    async createThread(documentUid, thread) {
        const serialized = serializeThreadData(omit(thread, ["messages"]));
        await this.db.documentChatThreadsCollection(documentUid).doc(thread.uid).set(serialized);
    }
    /** @inheritDoc */
    async setThread(documentUid, thread) {
        const serialized = serializeThreadData(omit(thread, ["messages"]));
        await this.db.documentChatThreadsCollection(documentUid).doc(thread.uid).set(serialized, { merge: true });
    }
    /** @inheritDoc */
    async updateThread(documentUid, threadUid, data) {
        // Use set() with {merge: true} instead of update() so the converter's toFirestore()
        // is called, which wraps the data in {data: ..., metadata: ...} structure.
        // Firestore's update() bypasses the converter, writing directly to document root.
        // Type assertion needed because Firestore's {merge: true} accepts DeepPartial at runtime,
        // but TypeScript's Partial<T> only makes top-level fields optional.
        const serialized = serializeThreadData(data);
        await this.db.documentChatThreadsCollection(documentUid).doc(threadUid).set(serialized, { merge: true });
    }
    /** @inheritDoc */
    async updateThreadExecutionTimestamp(documentUid, threadUid, timestamp) {
        // Use set() with {merge: true} to update ONLY the executionUpdatedAt field
        // without replacing other fields in latest_brief (like brief_status, content, stopRequested)
        // Type assertion needed because Firestore's {merge: true} accepts partial nested data at runtime,
        // but TypeScript's Partial<T> only makes top-level fields optional
        const data = { latest_brief: { executionUpdatedAt: timestamp } };
        await this.db.documentChatThreadsCollection(documentUid).doc(threadUid).set(data, { merge: true });
    }
    /** @inheritDoc */
    async deleteThread(documentUid, threadUid) {
        const messages = await this.db.documentChatMessagesCollection(documentUid, threadUid).get();
        await Promise.all(messages.docs.map((message) => this.db.documentChatMessagesCollection(documentUid, threadUid).doc(message.id).delete()));
        const deleteReactionsPromises = [];
        for (const message of messages.docs) {
            const reactions = await this.db
                .documentChatMessageReactionsCollection(documentUid, threadUid, message.id)
                .get();
            deleteReactionsPromises.push(...reactions.docs.map((reaction) => this.db
                .documentChatMessageReactionsCollection(documentUid, threadUid, message.id)
                .doc(reaction.id)
                .delete()));
        }
        await Promise.all(deleteReactionsPromises);
        await this.db.documentChatThreadsCollection(documentUid).doc(threadUid).delete();
    }
    /** @inheritDoc */
    async getMessage(documentUid, threadUid, messageUid) {
        return oneFromSnapshot(await this.db.documentChatMessagesCollection(documentUid, threadUid).doc(messageUid).get(), () => undefined);
    }
    /** @inheritDoc */
    async getMessages(documentUid, threadUid) {
        return allFromSnapshot(await this.db.documentChatMessagesCollection(documentUid, threadUid).get());
    }
    /** @inheritDoc */
    async createMessage(documentUid, threadUid, message) {
        await this.db
            .documentChatMessagesCollection(documentUid, threadUid)
            .doc(message.uid)
            .set(omit(message, ["reactions"]));
    }
    /**
     * Create a message with custom metadata strategy.
     * This is used by managed services (e.g., agent-chat-app) to inject custom metadata
     * that identifies the write as coming from a managed service.
     *
     * Note: This method requires the FirestoreAdapter to support the documentChatMessagesCollection
     * method with a metadataStrategy parameter (available in AdminFirestoreAdapter).
     */
    async createMessageWithMetadata(documentUid, threadUid, message, metadataStrategy) {
        await this.db
            .documentChatMessagesCollection(documentUid, threadUid, metadataStrategy)
            .doc(message.uid)
            .set(omit(message, ["reactions"]));
    }
    /** @inheritDoc */
    async setMessage(documentUid, threadUid, message) {
        await this.db
            .documentChatMessagesCollection(documentUid, threadUid)
            .doc(message.uid)
            .set(omit(message, ["reactions"]), { merge: true });
    }
    /** @inheritDoc */
    async deleteMessage(documentUid, threadUid, messageUid) {
        await this.db.documentChatMessagesCollection(documentUid, threadUid).doc(messageUid).delete();
        const reactions = await this.db
            .documentChatMessageReactionsCollection(documentUid, threadUid, messageUid)
            .get();
        await Promise.all(reactions.docs.map((reaction) => this.db
            .documentChatMessageReactionsCollection(documentUid, threadUid, messageUid)
            .doc(reaction.id)
            .delete()));
    }
    /** @inheritDoc */
    async getMessageReactions(documentUid, threadUid, messageUid) {
        return allFromSnapshot(await this.db.documentChatMessageReactionsCollection(documentUid, threadUid, messageUid).get());
    }
    /** @inheritDoc */
    async getMessageReaction(documentUid, threadUid, messageUid, reactionUid) {
        return oneFromSnapshot(await this.db
            .documentChatMessageReactionsCollection(documentUid, threadUid, messageUid)
            .doc(reactionUid)
            .get(), () => undefined);
    }
    /** @inheritDoc */
    async createReaction(documentUid, threadUid, messageUid, reaction) {
        await this.db
            .documentChatMessageReactionsCollection(documentUid, threadUid, messageUid)
            .doc(reaction.uid)
            .set(reaction);
    }
    /** @inheritDoc */
    async setReaction(documentUid, threadUid, messageUid, reaction) {
        await this.db
            .documentChatMessageReactionsCollection(documentUid, threadUid, messageUid)
            .doc(reaction.uid)
            .set(reaction, { merge: true });
    }
    /** @inheritDoc */
    async deleteReaction(documentUid, threadUid, messageUid, reactionUid) {
        await this.db
            .documentChatMessageReactionsCollection(documentUid, threadUid, messageUid)
            .doc(reactionUid)
            .delete();
    }
    /** @inheritDoc */
    subscribeToAllThreads(documentUid, onReceive, onDelete, onError) {
        return this.db.documentChatThreadsCollection(documentUid).onSnapshot((snap) => {
            const addedOrChanged = [];
            const deleted = [];
            for (const change of snap.docChanges()) {
                const deserialized = deserializeThreadData(change.doc.data());
                if (change.type === "removed") {
                    deleted.push(deserialized);
                }
                else {
                    addedOrChanged.push(deserialized);
                }
            }
            onReceive(addedOrChanged);
            onDelete(deleted);
        }, onError);
    }
    /** @inheritDoc */
    subscribeToThreadMessages(documentUid, threadUid, onReceive, onDelete, onError) {
        return this.db.documentChatMessagesCollection(documentUid, threadUid).onSnapshot((snap) => {
            const addedOrChanged = [];
            const deleted = [];
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            snap.docChanges().forEach((change) => {
                if (change.type === "removed") {
                    deleted.push(change.doc.data());
                }
                else {
                    addedOrChanged.push(change.doc.data());
                }
            });
            onReceive(addedOrChanged);
            onDelete(deleted);
        }, onError);
    }
    subscribeToMessageReactions(documentUid, threadUid, messageUid, onReceive, onDelete, onError) {
        return this.db.documentChatMessageReactionsCollection(documentUid, threadUid, messageUid).onSnapshot((snap) => {
            const addedOrChanged = [];
            const deleted = [];
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            snap.docChanges().forEach((change) => {
                if (change.type === "removed") {
                    deleted.push(change.doc.data());
                }
                else {
                    addedOrChanged.push(change.doc.data());
                }
            });
            onReceive(addedOrChanged);
            onDelete(deleted);
        }, onError);
    }
}
//# sourceMappingURL=FirestoreChatRepository.js.map